import React, {useContext, useEffect, useState} from 'react';
import {Button, Col, Form, Input, Modal, Row, Select, Table} from "antd";
import {FormattedMessage, useIntl} from "react-intl";
import {
    Treatment,
    TreatmentCreate,
    TreatmentUpdate
} from "../../../api-client";
import {CaretDownOutlined, DeleteOutlined, EditOutlined} from "@ant-design/icons";
import {useForm} from "antd/es/form/Form";
import {getTreatmentsApi, getNZIZApi} from "../../../api/apiInstances";
import {DentobookApplicationContext} from "../../../context/DentobookApplicationContext";
import logger from "../../../utils/logger";

interface ClinicTreatmentPlanCardProps {
    showModal: boolean;
    onClose: () => void;
    keyword: string;
}

const ClinicTreatmentPlanCard: React.FC<ClinicTreatmentPlanCardProps> = ({
                                                                             showModal, onClose, keyword
                                                                         }) => {
    const intl = useIntl();
    const [form] = useForm();
    const {locale} = useContext(DentobookApplicationContext);
    const {Option} = Select;
    const [newTreatmentName, setNewTreatmentName] = useState<string>('');
    const [newNzisCondition, setNewNzisCondition] = useState<string>('');
    const [newTreatmentPrice, setNewTreatmentPrice] = useState<string>('0');
    const [newTreatmentLaboratoryPrice, setNewTreatmentLaboratoryPrice] = useState<number>(0);
    const [editTreatmentId, setEditTreatmentId] = useState<string>('');
    const [editMode, setEditMode] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [treatments, setTreatments] = useState<Treatment[]>([]);
    const [displayedTreatments, setDisplayedTreatments] = useState<Treatment[]>([]);
    const [nzisDentalActivityCodes, setNzisDentalActivityCodes] = useState<Array<any>>([]);
    const { userOrganization } = React.useContext(DentobookApplicationContext);
    const treatmentsApi = getTreatmentsApi();
    const nzisApi = getNZIZApi();

    const showEditModal = (treatment: Treatment) => {
        if (treatment) {
            setEditTreatmentId(treatment.id ?? '');
            setNewTreatmentName(treatment.displayName ?? '');
            setNewNzisCondition(treatment.nzisCondition ?? '');
            setNewTreatmentPrice(treatment.price?.toString() ?? '0');
            setNewTreatmentLaboratoryPrice(typeof treatment.laboratoryPrice === 'number' ? treatment.laboratoryPrice : Number(treatment.laboratoryPrice) || 0);
            form.setFieldsValue({
                treatment: treatment.displayName,
                nzisTreatment: treatment.nzisCondition,
                price: treatment.price
            });
            setEditMode(true);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Reset form when opening modal for adding new treatment
    useEffect(() => {
        if (showModal && !editMode) {
            // Reset all fields to default values when adding new treatment
            setNewTreatmentName('');
            setNewNzisCondition('');
            setNewTreatmentPrice('0');
            setNewTreatmentLaboratoryPrice(0);
            form.setFieldsValue({
                treatment: '',
                nzisTreatment: '',
                price: ''
            });
        }
    }, [showModal, editMode, form]);

    const loadData = () => {

        treatmentsApi.fetchMedicalTreatments()
            .then(response => {
                const compFunc = (treatmentOne: Treatment, treatmentTwo: Treatment) =>
                    (treatmentOne.displayName ?? '').localeCompare(treatmentTwo.displayName ?? '');
                setTreatments(response.data.sort(compFunc));
                setDisplayedTreatments(response.data);
                nzisApi.fetchDentalActivityTypes()
                    .then(res => {
                        setNzisDentalActivityCodes(res.data);
                    })
                    .catch(err => logger.log(err))
            })
            .catch(error => logger.error("Failed to load treatment:", error));
    };

    useEffect(() => {
        if (keyword) {
            const filteredRooms = treatments.filter(treatment => treatment.displayName?.toLowerCase().includes(keyword.toLowerCase()));
            setDisplayedTreatments(filteredRooms);
        } else {
            setDisplayedTreatments(treatments);
        }
    }, [treatments, keyword]);


    const showDeleteModal = (treatmentId: string) => {
        setEditTreatmentId(treatmentId);
        setIsDeleteModalVisible(true);
    };

    const resetState = () => {
        setEditTreatmentId('');
        setNewTreatmentName('');
        setNewNzisCondition('');
        setNewTreatmentPrice('0');
        setNewTreatmentLaboratoryPrice(0);
        setEditMode(false);
        form.setFieldsValue({
            treatment: '',
            nzisTreatment: ''
        });
        setIsDeleteModalVisible(false);
    };


    const closeAddTreatmentModal = () => {
        onClose();
    };

    const handleCancel = () => {
        closeAddTreatmentModal();
        setEditTreatmentId('');
        setNewTreatmentName('');
        setNewNzisCondition('');
        setNewTreatmentPrice('0');
        setNewTreatmentLaboratoryPrice(0);
        setEditMode(false);
    };


    const handleAddOk = async () => {
        try {
            await form.validateFields();
            await handleCreatetreatment();
            closeAddTreatmentModal();
        } catch (err) {
            logger.log(err);
        }
    };

    const handleCreatetreatment = async () => {
        // Build proper TreatmentCreate DTO
        const createRequest: TreatmentCreate = {
            name: locale === 'BG' ? newTreatmentName : '',
            nameEn: locale === 'BG' ? null : newTreatmentName,
            nzisCondition: newNzisCondition || null,
            price: newTreatmentPrice || null,
            laboratoryPrice: newTreatmentLaboratoryPrice.toString() || null,
            organizationId: userOrganization?.id || ''
        };

        try {
            await treatmentsApi.createMedicalTreatment(createRequest);
            loadData();
        } catch (error) {
            logger.error("Failed to create treatment:", error);
        }
        resetState();
    };

    const handleEditTreatmentOk = async () => {
        try {
            await form.validateFields();

            // Build proper TreatmentUpdate DTO
            const updateRequest: TreatmentUpdate = {
                name: locale === 'BG' ? newTreatmentName : null,
                nameEn: locale === 'BG' ? null : newTreatmentName,
                nzisCondition: newNzisCondition || null,
                price: newTreatmentPrice || null,
                laboratoryPrice: newTreatmentLaboratoryPrice.toString() || null
            };

            try {
                if (editTreatmentId != null) {
                    await treatmentsApi.updateMedicalTreatment(editTreatmentId, updateRequest);
                }
                loadData();
            } catch (error) {
                logger.error("Failed to update treatment:", error);
            }
            resetState();
        } catch (err) {
            logger.log(err);
        }
    };

    const handleEditTreatmentCancel = () => {
        resetState();
    };

    const handleDeletetreatmentOk = async () => {
        try {
            if (editTreatmentId != null) {
                await treatmentsApi.deleteMedicalTreatment(editTreatmentId);
            }
            loadData();
        } catch (error) {
            logger.error("Failed to delete treatment:", error);
        }
        resetState();
    };

    const handleDeleteTreatmentCancel = () => {
        resetState();
    };

    const modalContent = <div className="medical-history-modal-group">
        <Form form={form} layout="vertical">
            <Form.Item
                name="treatment"
                rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                className="font-weight-400" label={<FormattedMessage id="naming"/>}>
                <Input
                    value={newTreatmentName}
                    onChange={(e) => setNewTreatmentName(e.target.value)}
                    className="custom-input"
                />
            </Form.Item>
            {userOrganization?.country === 'BG' && (
                <Form.Item
                    name="nzisTreatment"
                    rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                    className="dentobook-form-item dentobook-simple-input"
                    label={<FormattedMessage id="nzis_treatment_label"/>}>
                    <Select
                        suffixIcon={<CaretDownOutlined/>}
                        size="large"
                        className="dentobook-input"
                        value={newNzisCondition}
                        onChange={(value) => setNewNzisCondition(value)}
                    >
                        {nzisDentalActivityCodes.sort((a, b) => a.descriptionBG.localeCompare(b.descriptionBG))
                            .map(activity => (
                                <Option key={activity.key} value={activity.key}>
                                    {activity.descriptionBG}
                                </Option>
                            ))}
                    </Select>
                </Form.Item>
            )}
            <Form.Item
                rules={[
                    {
                        pattern: /(^[0-9]+\.[0-9]+$)|(^[0-9]+$)/,
                        message: <FormattedMessage id="digits_only_field"/>
                    }
                ]}
                name="price"
                className="font-weight-400"
                label={<FormattedMessage id="price"/>}>
                <Input
                    value={newTreatmentPrice}
                    onChange={(e) => setNewTreatmentPrice(e.target.value)}
                    className="custom-input"
                />
            </Form.Item>
        </Form>
    </div>;

    return (
        <>
            <Table dataSource={displayedTreatments}
                   pagination={false}
                   className="dentobook-table"
                   style={{marginTop: '5px'}}
            >
                <Table.Column key="id"
                              className={'custom-column-style'}
                              title={<FormattedMessage id="operation"/>}
                              sorter={(treatmentOne: Treatment, treatmentTwo: Treatment) =>
                                  (treatmentOne.displayName ?? '').localeCompare(treatmentTwo.displayName ?? '')}
                              render={(treatment) => <span
                                  className="font-weight-500">{treatment.displayName}</span>}
                />
                {userOrganization?.country === 'BG' && (
                <Table.Column key="nzisName"
                              className={'custom-column-style'}
                              title={<FormattedMessage id="nzis_treatment_label"/>}
                              render={(treatment) => <span
                                  className="font-weight-500">{nzisDentalActivityCodes.find(activityCode => activityCode.key === treatment.nzisCondition)?.descriptionBG}</span>}
                />)}
                <Table.Column key="price"
                              className={'custom-column-style'}
                              title={<FormattedMessage id="price"/>}
                              render={(treatment) => <span className="font-weight-500">{treatment.price}</span>}
                />
                <Table.Column key="color"
                              title={<div></div>}
                              className={'custom-column-style'}
                              render={() => (
                                  <div style={{display: 'flex', alignItems: 'center'}}>
                                  </div>
                              )}

                />
                <Table.Column key="actions"
                              title={<div></div>}
                              className={'custom-column-style'}
                              render={(treatment) => {
                                  return (
                                      <div style={{display: 'flex', flexDirection: 'row', alignItems: 'right'}}>
                                          <Button className="text-black font-20 ml-1" type="link"
                                                  icon={<EditOutlined/>}
                                                  style={{marginRight: '10px'}}
                                                  onClick={() => showEditModal(treatment)}/>

                                          <Button className="text-black font-20" type="link"
                                                  icon={<DeleteOutlined/>}
                                                  onClick={() => showDeleteModal(treatment.id as string)}/>
                                      </div>
                                  );
                              }}/>
            </Table><Modal
            transitionName=""
            okButtonProps={{
                className: "modal-btn"
            }}
            cancelButtonProps={{
                className: "modal-btn"
            }}
            title={<FormattedMessage id="operation"/>}
            open={showModal}
            onOk={handleAddOk}
            onCancel={handleCancel}
            className="dentobook-modal"
        >
            {modalContent}
        </Modal><Modal
            transitionName=""
            okButtonProps={{
                className: "modal-btn"
            }}
            cancelButtonProps={{
                className: "modal-btn"
            }}
            title={<FormattedMessage id="editTreatment"/>}
            okText={intl.formatMessage({id: 'save'})}
            cancelText={intl.formatMessage({id: 'cancel'})}
            open={editMode}
            onOk={handleEditTreatmentOk}
            onCancel={handleEditTreatmentCancel}
            className="dentobook-modal"
        >
            {modalContent}
        </Modal>

            {/* Delete Modal */}
            <Modal
                transitionName=""
                okButtonProps={{
                    className: "modal-btn",
                    danger: true,
                    type: "primary",
                }}
                cancelButtonProps={{
                    className: "modal-btn"
                }}
                className="dentobook-modal" title={<FormattedMessage id="deleteTreatment"/>}
                okText={intl.formatMessage({id: 'delete'})}
                cancelText={intl.formatMessage({id: 'cancel'})}
                open={isDeleteModalVisible} onOk={handleDeletetreatmentOk}
                onCancel={handleDeleteTreatmentCancel}
            >
                <p><FormattedMessage id="tratmentDeleteConfirmation"/></p>
            </Modal>
        </>
    );
};
export default ClinicTreatmentPlanCard;
